import React, { useState, useMemo } from 'react';
import { Button, LoadingState } from '@/shared';
import { Link } from '@tanstack/react-router';
import {
  NoResourceData,
  ResourceDataTable,
  ResourcesErrorBoundary,
} from '../components';
import { AddResourceModal } from '../components/modals';
import { useResources, useResourcesOverview } from '../hooks/useResources';

// ---------- Utility ----------
const formatNumber = (num: number): string =>
  num >= 1000 ? `${(num / 1000).toFixed(1)}k` : num.toString();

// ---------- Component ----------
const ResourcesPageContent: React.FC = () => {
  const [showAddResource, setShowAddResource] = useState<boolean>(false);

  // Fetch Overview
  const { data: overviewData, isLoading: isOverviewLoading } =
    useResourcesOverview();

  // Fetch Resources List
  const { data: resourcesData, isLoading: isResourcesLoading } = useResources(
    1,
    10,
  );

  const processedCards = useMemo(() => {
    if (!overviewData) return [];

    const { categoryCounts } = overviewData;

    return [
      { title: 'Guides & manuals', value: categoryCounts?.GUIDE || 0 },
      {
        title: 'Policies & Legal Docs',
        value: categoryCounts?.POLICY_DOCUMENT || 0,
      },
      { title: 'Templates', value: categoryCounts?.TEMPLATE || 0 },
      { title: 'External links', value: categoryCounts?.EXTERNAL_LINK || 0 },
    ].map((card) => ({
      ...card,
      formattedValue: formatNumber(card.value),
    }));
  }, [overviewData]);

  if (isOverviewLoading || isResourcesLoading) {
    return <LoadingState message="Loading resources..." fullScreen />;
  }

  const total = overviewData?.totalResources || 0;

  return (
    <div className="min-h-screen bg-background w-full">
      {total === 0 && !isOverviewLoading && !overviewData ? (
        <NoResourceData />
      ) : (
        <div>
          {/* Header */}
          <div className="mb-4 md:mb-8 px-0 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-2 py-2">
              <p className="text-xl text-text-secondary font-bold">
                Total resources
              </p>
            </div>

            {/* Summary */}
            <div className="flex items-center gap-4">
              <span className="text-5xl text-text-primary font-bold">
                {total}
              </span>
            </div>

            {/* Progress Bars */}
            <div className="grid grid-cols-12 gap-2 my-4 md:my-9">
              <div className="h-[6px] bg-[#B6B0FB] col-span-4" />
              <div className="h-[6px] bg-[#BBEEED] col-span-1" />
              <div className="h-[6px] bg-[#FBDE9D] col-span-3" />
              <div className="h-[6px] bg-[#95A1FC] col-span-4" />
            </div>

            {/* Overview Cards */}
            <div className="grid grid-cols-2 gap-4">
              {processedCards.map((card, idx) => (
                <div key={idx} className="p-3 bg-white rounded-md shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="bg-[#B6B0FB] h-2 w-2 rounded-full" />
                    <span className="text-text-secondary text-sm md:text-base">{card.title}</span>
                  </div>
                  <span className="text-base md:text-xl text-text-primary font-bold">
                    {card.formattedValue}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-4 md:mb-8 px-0 sm:px-6 lg:px-8">
            <div className="w-full flex flex-col md:flex-row items-center justify-center md:justify-between gap-4 p-4 card">
              <div className="flex items-center gap-6">
                <svg
                  width="40"
                  height="40"
                  viewBox="0 0 40 40"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M29.9948 21.667V18.3337H36.6615V21.667H29.9948ZM31.9948 33.3337L26.6615 29.3337L28.6615 26.667L33.9948 30.667L31.9948 33.3337ZM28.6615 13.3337L26.6615 10.667L31.9948 6.66699L33.9948 9.33366L28.6615 13.3337ZM8.32812 31.667V25.0003H6.66146C5.74479 25.0003 4.96007 24.6739 4.30729 24.0212C3.65451 23.3684 3.32812 22.5837 3.32812 21.667V18.3337C3.32812 17.417 3.65451 16.6323 4.30729 15.9795C4.96007 15.3267 5.74479 15.0003 6.66146 15.0003H13.3281L21.6615 10.0003V30.0003L13.3281 25.0003H11.6615V31.667H8.32812ZM23.3281 25.5837V14.417C24.0781 15.0837 24.6823 15.8962 25.1406 16.8545C25.599 17.8128 25.8281 18.8614 25.8281 20.0003C25.8281 21.1392 25.599 22.1878 25.1406 23.1462C24.6823 24.1045 24.0781 24.917 23.3281 25.5837Z"
                    fill="#5761EB"
                  />
                </svg>

                <div>
                  <p className="text-base md:text-xl text-text-primary font-bold">
                    Add new resource
                  </p>
                  <p className="text-base md:text-xl text-text-secondary">
                    Manage your properties with ease
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-8">
                <Button
                  className="!text-base md:text-lg !font-bold px-6 py-3"
                  onClick={() => setShowAddResource(true)}
                >
                  Add resource
                </Button>
              </div>
            </div>
          </div>

          <div className="mb-4 md:mb-8 px-0 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-text-primary text-lg md:text-xl font-bold">Resources</h3>
              <Link
                to="/resources/all"
                className="text-[#357AE1] cursor-pointer hover:underline"
              >
                See more
              </Link>
            </div>
          </div>

          <div className="overflow-x-auto">
            <ResourceDataTable data={resourcesData?.resources || []} className='min-h-[50vh]' />
          </div>

          {showAddResource && (
            <AddResourceModal
              isOpen={showAddResource}
              onClose={() => setShowAddResource(!showAddResource)}
            />
          )}
        </div>
      )}
    </div>
  );
};

const ResourcesPage: React.FC = () => {
  return (
    <ResourcesErrorBoundary onRetry={() => window.location.reload()}>
      <ResourcesPageContent />
    </ResourcesErrorBoundary>
  );
};

export default ResourcesPage;
